import React from 'react';
import { X, Bell } from 'lucide-react';

interface SimulatedUser {
  id: string;
  name: string;
  role: string;
  detail: string;
  avatar: string;
  color: string;
  email: string;
}

interface SimulatedUserCardSelectorProps {
  roleKey: string;
  theme: 'dark' | 'light' | 'purple';
  onClose: () => void;
  onSelectUser: (user: SimulatedUser) => void;
}

export const MOCK_USERS_BY_ROLE: Record<string, SimulatedUser[]> = {
  administrador: [
    { id: 'usr-admin-1', name: 'Euler Magalhães', role: 'Administrador', detail: 'Gestão de TI · Auditoria Geral', avatar: 'EM', color: '#9b6cff', email: 'euler@alunoconectado.com.br' }
  ],
  prefeito: [
    { id: 'usr-prefeito-1', name: 'Dr. Marcos Silva', role: 'Prefeito', detail: 'Prefeito Municipal · Cabo de Santo Agostinho', avatar: 'MS', color: '#d8ad54', email: 'gabinete.cabo@municipio.gov.br' },
    { id: 'usr-prefeito-2', name: 'Amanda Rocha', role: 'Prefeita', detail: 'Prefeita Municipal · Bambuí', avatar: 'AR', color: '#d8ad54', email: 'prefeita.amanda@bambui.mg.gov.br' }
  ],
  diretor: [
    { id: 'usr-diretor-1', name: 'Prof. Marcelo Duretti', role: 'Diretor', detail: 'Diretor Geral · Escola Municipal Aluno Conectado', avatar: 'MD', color: '#5971ff', email: 'marcelo.duretti@escola.edu.br' },
    { id: 'usr-diretor-2', name: 'Sandra Souza', role: 'Diretora', detail: 'Diretora Pedagógica · Escola Presidente Vargas', avatar: 'SS', color: '#5971ff', email: 'sandra.souza@escolapv.edu.br' }
  ],
  coordenador: [
    { id: 'usr-coordenador-1', name: 'Regina Alves', role: 'Coordenador', detail: 'Coordenadora de Ensino · Rede Municipal', avatar: 'RA', color: '#2ccbba', email: 'regina.alves@rede.cabo.gov.br' }
  ],
  secretaria: [
    { id: 'usr-secretaria-1', name: 'Patrícia Melo', role: 'Secretária', detail: 'Secretária Geral · Escola Municipal AC', avatar: 'PM', color: '#ff7785', email: 'patricia.melo@escola.edu.br' }
  ],
  professor: [
    { id: 'usr-professor-1', name: 'Prof. Ricardo Lima', role: 'Professor', detail: 'Professor de Matemática · Ensino Fundamental', avatar: 'RL', color: '#58e19d', email: 'ricardo.matematica@escola.edu.br' }
  ],
  responsavel: [
    { id: 'usr-responsavel-1', name: 'João de Oliveira', role: 'Responsável', detail: 'Responsável Financeiro · Dependente: Arthur', avatar: 'JO', color: '#ff66a9', email: 'joao.oliveira@gmail.com' }
  ],
  aluno: [
    { id: 'usr-aluno-1', name: 'Arthur Oliveira', role: 'Aluno', detail: 'Aluno Matriculado · 9º Ano B', avatar: 'AO', color: '#775fff', email: 'arthur.oliveira@estudante.com.br' }
  ]
};

export const SimulatedUserCardSelector: React.FC<SimulatedUserCardSelectorProps> = ({
  roleKey,
  theme,
  onClose,
  onSelectUser,
}) => {
  const users = MOCK_USERS_BY_ROLE[roleKey.toLowerCase()] || [];

  const themeStyles = {
    purple: {
      bg: 'bg-[#181825]/95 border-[#4c1d95]/40 text-[#cdd6f4]',
      cardBg: 'bg-[#1e1e2e]/80 border-white/5 hover:border-[#4c1d95]/60 hover:bg-[#4c1d95]/10',
      headerBg: 'bg-[#0f0a21]/80 border-b border-[#4c1d95]/30',
      textMuted: 'text-[#a6adc8]',
    },
    dark: {
      bg: 'bg-[#121215]/95 border-white/5 text-[#e0e0e0]',
      cardBg: 'bg-[#1c1c20]/80 border-white/5 hover:border-white/20 hover:bg-white/5',
      headerBg: 'bg-[#0a0a0c]/80 border-b border-white/5',
      textMuted: 'text-[#888888]',
    },
    light: {
      bg: 'bg-[#ffffff]/95 border-[#cbd5e1] text-[#0f172a]',
      cardBg: 'bg-[#f8fafc] border-[#cbd5e1]/60 hover:border-[#64748b] hover:bg-white',
      headerBg: 'bg-[#f1f5f9] border-b border-[#cbd5e1]',
      textMuted: 'text-[#64748b]',
    }
  }[theme];

  const handleUserClick = (user: SimulatedUser) => {
    // 1. Aciona permissão de notificação em segundo plano sem bloquear a simulação
    if ('Notification' in window && Notification.permission === 'default') {
      console.log('[Simulador] Solicitando permissões de notificação...');
      Notification.requestPermission()
        .then((permission) => {
          if (permission === 'granted') {
            console.log('[Simulador] Permissão concedida pelo usuário.');
            new Notification('Notificações Ativas', {
              body: `As notificações para o perfil de ${user.role} foram ativadas com sucesso!`,
              icon: '/favicon.ico'
            });
          }
        })
        .catch((err) => {
          console.warn('[Simulador] Erro de notificação:', err);
        });
    }

    // 2. Aciona o login automático imediatamente
    onSelectUser(user);
  };

  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-md z-[99999] flex items-center justify-center p-4">
      <div className={`w-[580px] max-w-full rounded-3xl border shadow-2xl flex flex-col overflow-hidden ${themeStyles.bg}`}>
        {/* Header */}
        <div className={`px-6 py-4 flex items-center justify-between ${themeStyles.headerBg}`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">👤</span>
            <div className="flex flex-col text-left">
              <span className="text-xs font-bold uppercase tracking-wider text-white">Escolher Perfil de Trabalho</span>
              <span className={`text-[9px] ${themeStyles.textMuted}`}>Selecione um usuário para simular e iniciar login automático</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-xl flex items-center justify-center hover:bg-white/10 text-white transition-all cursor-pointer border-none bg-transparent active:scale-90"
          >
            <X size={16} />
          </button>
        </div>

        {/* User Cards Grid */}
        <div className="p-6 overflow-y-auto max-h-[380px] grid grid-cols-1 sm:grid-cols-2 gap-4">
          {users.length === 0 ? (
            <div className={`col-span-2 py-8 text-center text-[10px] font-bold ${themeStyles.textMuted}`}>
              Nenhum usuário simulado encontrado para este perfil.
            </div>
          ) : (
            users.map((user) => (
              <div
                key={user.id}
                onClick={() => handleUserClick(user)}
                className={`p-4 rounded-2xl border transition-all cursor-pointer flex gap-3 text-left items-center group active:scale-95 ${themeStyles.cardBg}`}
              >
                {/* Avatar Orb */}
                <div 
                  className="w-11 h-11 rounded-2xl flex items-center justify-center text-xs font-black text-white shadow-md transition-all group-hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${user.color}e6 0%, ${user.color}80 100%)`,
                    boxShadow: `0 4px 12px ${user.color}25`
                  }}
                >
                  {user.avatar}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-[11px] font-bold text-white group-hover:text-white transition-colors truncate">{user.name}</h4>
                  <span className="text-[8px] font-extrabold opacity-65 tracking-wider uppercase block mt-0.5" style={{ color: user.color }}>
                    {user.role}
                  </span>
                  <span className={`text-[7px] truncate block mt-1 ${themeStyles.textMuted}`}>{user.detail}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer info */}
        <div className={`px-6 py-4 bg-black/15 text-[8px] font-semibold text-center flex items-center justify-center gap-1.5 ${themeStyles.textMuted}`}>
          <Bell size={10} className="text-[#a78bfa]" />
          <span>O simulador concederá permissões de notificação local para o perfil selecionado.</span>
        </div>
      </div>
    </div>
  );
};
